// ==================================================
// FILE: themes/archboundTheme.js
// ==================================================

/**
 * Archbound Legacy - Gods Among Us
 * 
 * Humanity's ancient myths were not myths at all. Across the eons, civilizations—from Atlantis to Egypt,
 * Sumeria to Mesoamerica—worshiped Eldritch deities, beings whose consciousness spanned the stars and whose
 * power bent reality itself. These gods granted knowledge, constructed Archways—monolithic portals linking
 * worlds—and enslaved humanity across countless planets.
 * 
 * But some humans rebelled. The Atlantean uprising sparked a war that shook the foundations of gods and mortals alike.
 * Atlantis sank, taking the main Archway and centuries of forbidden knowledge with it. Minor Arches were buried,
 * hidden by rebellious civilizations like Egypt and Sumeria, their locations lost to time. The gods retreated, but never
 * disappeared—lurking in shadows, waiting.
 * 
 * Millennia later, Nazi excavations uncovered a minor Arch during World War II. Post-war, the Allies secured and
 * concealed it, unaware of its true nature. For sixty years it lay dormant beneath a U.S. military base, a silent
 * testament to humanity's ignorance. Until now. A brilliant archaeologist deciphered an ancient text and activated
 * the Archway, awakening the gods' lingering Spawn parasites and revealing that the Eldritch deities still exist—
 * and still enslave humanity across the universe.
 * 
 * The Arch Network is no longer dormant. Hidden temples, buried gateways, and ancient inscriptions pulse with energy
 * and menace. The U.S. military must cooperate with scientists, historians, and explorers to navigate the ruins of
 * lost civilizations, prevent cosmic horrors from escaping into our reality, and uncover Atlantean secrets before
 * the gods' vengeance spreads across worlds. Every Archway is a doorway to wonder, terror, and war—and humanity
 * may yet rise, or be consumed.
 */

import { 
  Eye, 
  Radio, 
  Settings, 
  BookOpen, 
  Zap,
  FlaskConical,
  Users,
  Compass
} from 'lucide-react';

const archboundTheme = {
  id: 'archbound',
  
  // Landing Page Configuration
  title: {
    line1: 'ARCHBOUND',
    line2: 'LEGACY',
    line1Color: 'text-purple-400',
    line2Color: 'text-indigo-600'
  },
  subtitle: 'ARCH NETWORK COMMAND',
  
  colors: {
    primary: 'purple',
    secondary: 'indigo',
    accent: 'violet',
    background: 'from-slate-900 via-purple-950 to-black'
  },
  
  backgroundImage: {
    url: '/images/archbound-background.jpg',
    opacity: 0.3,
    blend: 'multiply',
    position: 'center',
    size: 'cover'
  },
  
  userTitle: 'COMMANDER',
  userTitleGuest: 'UNAUTHORIZED ACCESS',
  clearanceLevel: 'CLASSIFIED',
  
  alert: {
    text: 'ARCH ANOMALY DETECTED - ELDRITCH SIGNATURE CONFIRMED',
    color: 'purple'
  },
  
  menuItems: [
    {
      id: 'new',
      icon: Eye,
      label: 'NEW OPERATION',
      subtitle: 'Establish command at the Archway site',
      color: 'purple'
    },
    {
      id: 'load',
      icon: Radio,
      label: 'RESUME MISSION',
      subtitle: 'Return to active containment operations',
      color: 'indigo'
    }
  ],
  
  secondaryItems: [
    { id: 'settings', icon: Settings, label: 'CONFIGURATIONS' },
    { id: 'profile', icon: BookOpen, label: 'FIELD JOURNAL' },
    { id: 'news', icon: Radio, label: 'INTEL FEED' },
    { id: 'leaderboard', icon: Zap, label: 'OPERATION RECORDS' }
  ],
  
  support: {
    title: 'SUPPORT THE MISSION',
    description: 'Every contribution helps humanity uncover the truth behind the Archways.\nSupport the development of our defense against the Eldritch gods.',
    buttonText: 'SUPPORT RESEARCH',
    icon: Zap
  },
  
  footer: {
    status: 'OPERATIONAL',
    readyText: 'COMMAND CENTER ONLINE',
    signInText: 'CLEARANCE VERIFICATION REQUIRED'
  },
  
  // Base Operations Configuration
  loading: 'INITIALIZING ARCH PROTOCOLS...',
  baseTitle: 'COMMAND CENTER',
  startDate: '1945-05-08',
  
  tabs: {
    base: 'COMMAND',
    squad: 'OPERATIVES',
    research: 'RESEARCH',
    missions: 'EXPEDITIONS'
  },
  
  icons: {
    base: Eye,
    squad: Users,
    research: FlaskConical,
    missions: Compass
  },
  
  // Resource labels
  resources: {
    funds: 'FUNDING',
    supplies: 'MATERIEL',
    intel: 'INTEL',
    power: 'ARCHAIC ENERGY'
  },
  
  // Facility names
  facilities: {
    command: 'Arch Operations',
    barracks: 'Expedition Barracks',
    generator: 'Arch Capacitor',
    medbay: 'Medical Wing',
    research: 'Science Annex',
    workshop: 'Field Ops Supply'
  },
  
  // Squad Configuration
  squad: {
    title: 'OPERATIVE CORPS',
    personnelLabel: 'Active Operatives',
    recruitButton: 'RECRUIT OPERATIVE',
    recruitCost: 1000,
    firstRecruitButton: 'RECRUIT FIRST OPERATIVE',
    emptyTitle: 'No Operatives Assigned',
    emptyMessage: 'Recruit your first operative to begin exploring the Arch Network',
    manageButton: 'DOSSIER',
    healButton: 'TREAT INJURIES',
    healthLabel: 'VITALS',
    statusLabel: 'STATUS',
    missionsLabel: 'Expeditions Completed',
    experienceLabel: 'CLEARANCE',
    summaryTitle: 'Operative Corps Summary',
    statusLabels: {
      ready: 'Ready',
      wounded: 'Injured',
      training: 'Training',
      onMission: 'On Expedition'
    }
  },
  
  // Research Configuration
  research: {
    title: 'RESEARCH DIVISION',
    subtitle: 'Unlock ancient knowledge and develop countermeasures against Eldritch threats',
    
    // Tier Labels
    earlyTier: 'TIER 1: INITIAL DISCOVERIES',
    midTier: 'TIER 2: ATLANTEAN SECRETS',
    lateTier: 'TIER 3: ELDRITCH MASTERY',
    
    // Tier Descriptions
    earlyDesc: 'Basic understanding of Arch technology and Spawn biology',
    midDesc: 'Advanced Atlantean knowledge and enhanced containment protocols',
    lateDesc: 'Mastery of Eldritch power and reality manipulation',
    
    // Status Labels
    researchingLabel: 'RESEARCHING',
    prerequisitesLabel: 'PREREQUISITES',
    benefitsLabel: 'ADVANTAGES',
    unlocksLabel: 'TECHNOLOGIES UNLOCKED',
    
    // Action Buttons
    startButton: 'BEGIN RESEARCH',
    insufficientButton: 'INSUFFICIENT FUNDING',
    cancelButton: 'CANCEL',
    
    // Progress Stats
    totalCompletedLabel: 'Technologies Mastered',
    currentResearchLabel: 'Active Research',
    availableProjectsLabel: 'Ready for Study',
    ofLabel: 'of',
    projectsLabel: 'technologies',
    noneActiveLabel: 'Research idle',
    readyToResearchLabel: 'Ready to research',
    completedLabel: 'researched',
    availableLabel: 'Available',
    
    // Status Messages
    notAvailableLabel: 'CLASSIFIED',
    completedTag: 'RESEARCHED',
    lockedTag: 'LOCKED',
    inProgressTag: 'IN PROGRESS',
    
    // Project overrides for Archbound flavor
    projectOverrides: {
      basic_armor: {
        name: 'Reinforced Field Gear',
        description: 'Develop protective equipment resistant to Spawn attacks and Eldritch energies'
      },
      weapon_training: {
        name: 'Anti-Spawn Combat',
        description: 'Train operatives in tactics effective against parasitic entities'
      },
      field_medicine: {
        name: 'Xenobiology Protocols',
        description: 'Establish treatment procedures for Spawn infection and psychic trauma'
      },
      basic_recon: {
        name: 'Arch Detection',
        description: 'Deploy sensors to detect dormant Archways and Eldritch signatures'
      },
      advanced_armor: {
        name: 'Atlantean Alloy Plating',
        description: 'Reverse-engineer Atlantean metallurgy for superior protection'
      },
      weapon_mods: {
        name: 'Archaic Energy Weapons',
        description: 'Weaponize captured Arch energy against the gods\' servants'
      },
      combat_medic: {
        name: 'Field Xenobiologist',
        description: 'Train medics to treat exotic wounds and psychic contamination'
      },
      stealth_ops: {
        name: 'Psychic Dampening',
        description: 'Develop mental shielding to avoid Eldritch detection'
      },
      surveillance_network: {
        name: 'Global Arch Monitor',
        description: 'Network all detection systems to track worldwide Arch activity'
      },
      sniper_training: {
        name: 'Precision Elimination',
        description: 'Train marksmen to target Spawn weak points from safe distances'
      },
      powered_exoskeleton: {
        name: 'Atlantean Power Armor',
        description: 'Reconstruct ancient battle suits using recovered schematics'
      },
      energy_shields: {
        name: 'Reality Anchors',
        description: 'Generate fields that stabilize reality against Eldritch manipulation'
      },
      plasma_weapons: {
        name: 'Stellar Armaments',
        description: 'Harness star-fire technology from beyond the Archways'
      },
      railgun_tech: {
        name: 'Arch Accelerators',
        description: 'Use Archway dimensional physics to create devastating kinetic weapons'
      },
      nano_medicine: {
        name: 'Atlantean Nanites',
        description: 'Deploy ancient healing machines that repair tissue at molecular levels'
      },
      mobile_hospital: {
        name: 'Mobile Research Station',
        description: 'Establish field laboratories for on-site containment and treatment'
      },
      ghost_protocol: {
        name: 'Phase Shifting',
        description: 'Manipulate dimensional barriers to become intangible'
      },
      cyber_warfare: {
        name: 'Arch Network Hacking',
        description: 'Infiltrate and manipulate the interdimensional Arch communication system'
      },
      ai_tactical: {
        name: 'Atlantean AI Reconstruction',
        description: 'Restore ancient artificial minds to advise on Eldritch strategy'
      },
      strategic_planning: {
        name: 'Global Command Network',
        description: 'Coordinate all operations across every discovered Archway site'
      },
      titan_armor: {
        name: 'Project GODKILLER',
        description: 'The ultimate weapon: armor infused with captured divine essence'
      }
    }
  },
  
  // Missions Configuration
  missions: {
    title: 'EXPEDITION COMMAND',
    subtitle: 'Deploy operatives to investigate Archways and contain Eldritch threats',
    threatLabel: 'ELDRITCH THREAT',
    activeLabel: 'Active Expeditions',
    deployedLabel: 'operatives deployed',
    successLabel: 'SUCCESS',
    failedLabel: 'COMPROMISED',
    simpleTier: 'RECONNAISSANCE',
    complexTier: 'OPERATIONS',
    campaignTier: 'MAJOR EXPEDITIONS',
    autoOnly: 'RAPID DEPLOYMENT',
    autoOrManual: 'DEPLOYMENT CHOICE',
    manualOnly: 'TACTICAL CONTROL',
    squadRequirement: 'OPERATIVE REQUIREMENT',
    soldiersLabel: 'operatives',
    unlocksLabel: 'Available Day',
    insufficientSquad: 'Insufficient operatives available',
    minutesLabel: 'minutes',
    rewardsLabel: 'INTELLIGENCE',
    risksLabel: 'HAZARDS',
    casualtyLabel: 'Casualty Risk',
    chooseModeLabel: 'Choose Deployment Mode',
    autoResolveTitle: 'RAPID DEPLOYMENT',
    autoResolveDesc: 'Mission resolves based on operative capabilities. Quick and efficient.',
    manualControlTitle: 'TACTICAL CONTROL',
    manualControlDesc: 'Direct operative actions in the field. Maximum control and rewards.',
    selectSquadLabel: 'Select Expedition Team',
    requiredLabel: 'required',
    noSoldiersLabel: 'No operatives available for expedition',
    deployAutoButton: 'DEPLOY (AUTO)',
    deployManualButton: 'DEPLOY (TACTICAL)',
    cancelButton: 'ABORT',
    simpleNames: {
      supply: 'Supply Recovery',
      patrol: 'Perimeter Sweep',
      training: 'Field Exercises'
    },
    complexNames: {
      raid: 'Site Excavation',
      assassination: 'Spawn Elimination',
      sabotage: 'Arch Disruption',
      rescue: 'Civilian Extraction'
    },
    campaignNames: {
      vanguard: 'Operation Sunken Gate',
      liberation: 'The Atlantis Descent'
    }
  },
  
  // Mission Briefing Configuration
  missionBriefing: {
    title: 'EXPEDITION BRIEFING',
    alertText: 'PREPARE FOR DEPLOYMENT - EQUIPMENT VERIFICATION REQUIRED',
    abortButton: 'ABORT MISSION',
    
    intel: {
      title: 'Mission Intelligence',
      locationLabel: 'SITE LOCATION',
      durationLabel: 'ESTIMATED DURATION',
      threatLabel: 'THREAT ASSESSMENT',
      briefLabel: 'MISSION BRIEF',
      intelligenceLabel: 'INTELLIGENCE REPORT',
      objectivesLabel: 'OBJECTIVES',
      rewardsLabel: 'EXPECTED INTEL'
    },
    
    roster: {
      title: 'Expedition Team',
      healthLabel: 'VITALS'
    },
    
    loadout: {
      title: 'Equipment Manifest',
      selectPrompt: 'SELECT AN OPERATIVE',
      selectDesc: 'Choose an operative from the roster to review their loadout',
      statusLabel: 'MISSION READY',
      weaponSection: 'Primary Weapon',
      armorSection: 'Protective Gear',
      utilitySection: 'Field Equipment',
      damageLabel: 'DMG',
      accuracyLabel: 'ACC',
      defenseLabel: 'DEF',
      mobilityLabel: 'MOB'
    },
    
    deploy: {
      readyTitle: 'DEPLOYMENT AUTHORIZED',
      readyDesc: 'equipped and ready for field operations',
      beginButton: 'BEGIN EXPEDITION',
      warningText: '⚠️ WARNING: Expedition cannot be recalled once initiated. Casualties and psychic contamination possible.'
    }
  }
};

export default archboundTheme;